import { createStore } from "zustand/vanilla";
import type { StoreApi } from "zustand/vanilla";
import type { MapObject, TileFilterCapped, TileFilterField, TileFilterValues } from "@fortex/api";
import type { Catalogue } from "../catalogue/types";
import type { Feature } from "../features/types";
import type { InfoFeature, InfoHit, TrackPlaybackState } from "../sources/types";
import type { DrawMode, LatLngTuple, Theme } from "./options";

export type LayerStatus = "idle" | "loading" | "error";

export type LayerSnapshot = {
  id: string;
  key: string;
  root: "maps" | "layers" | "custom" | "track";
  kind: string;
  title: string;
  selected: boolean;
  opacity: number;
  count: number | null;
  countLabel: string | null;
  status: LayerStatus;
  error: string | null;
  httpStatus: number | null;
  minZoom: number | null;
  maxZoom: number | null;
  truncated: boolean;
  disabledReason: string | null;
};

export type DrawGeometry = {
  mode: DrawMode | null;
  wkt: string | null;
  points: LatLngTuple[];
  lengthMeters: number;
  areaSquareMeters: number;
  perimeterMeters: number;
  valid: boolean;
  reason: string | null;
};

export type DrawSnapshot = {
  geometry: DrawGeometry;
  phase: "idle" | "drawing" | "editing";
  canUndo: boolean;
  canRedo: boolean;
  snap: boolean;
  editable: boolean;
};

export type InfoState =
  | { kind: "closed" }
  | { kind: "loading"; objectId: number | null; hit: InfoHit | null }
  | { kind: "object"; object: MapObject; detail: unknown | null; error: string | null }
  | { kind: "cluster"; feature: Feature }
  | { kind: "multi"; objects: MapObject[] }
  | { kind: "tile"; feature: InfoFeature; dirty: boolean; saving: "idle" | "saving" | "saved" | "error"; error: string | null; searchTerm?: string | null }
  | { kind: "error"; message: string };

export type SearchState = {
  term: string;
  tab: "objects" | "address";
  loading: boolean;
  objects: MapObject[];
  addresses: { displayName: string; lat: number; lon: number; boundingBox: [LatLngTuple, LatLngTuple] | null }[];
  recent: MapObject[];
  error: string | null;
  filterFields: TileFilterField[];
  filters: TileFilterValues;
};

export type MeasureState = {
  mode: "distance" | "area" | null;
  points: LatLngTuple[];
  summary: string | null;
};

export type AddressHit = { displayName: string; lat: number; lon: number; boundingBox: [LatLngTuple, LatLngTuple] | null };

export type ResultItem = { kind: "object"; object: MapObject } | { kind: "feature"; hit: InfoHit; latlng: LatLngTuple | null; zoom: number | null } | { kind: "address"; hit: AddressHit };

export type ResultsState = {
  source: "search" | "click" | null;
  term: string;
  items: ResultItem[];
  capped?: TileFilterCapped[];
  loading: boolean;
  error: string | null;
};

export type SideTab = "layers" | "results" | "selected";

export type Toast = {
  id: number;
  severity: "info" | "success" | "warning" | "danger";
  text: string;
  action?: { label: string; run: () => void } | null;
};

export type PanelName = "layers" | "info" | "search" | "legend" | null;

export type MapStoreState = {
  ready: boolean;
  catalogue: Catalogue | null;
  catalogueError: string | null;
  layers: LayerSnapshot[];
  collapsed: string[];
  dark: boolean;
  theme: Theme;
  langId: number;
  view: { center: LatLngTuple; zoom: number; bounds: [LatLngTuple, LatLngTuple] | null };
  selection: { ids: number[]; objects: MapObject[] };
  hovered: MapObject | null;
  draw: DrawSnapshot;
  info: InfoState;
  search: SearchState;
  results: ResultsState;
  sideTab: SideTab;
  measure: MeasureState;
  panel: PanelName;
  panelPinned: boolean;
  tableLayer: string | null;
  inspectorModal: boolean;
  sideClosed: boolean;
  hint: { id: string; text: string } | null;
  coordinateSystem: "wgs84" | "eov";
  cursor: LatLngTuple | null;
  toasts: Toast[];
  errors: { id: string; message: string }[];
  editing: boolean;
  featureAccess: Record<string, "None" | "ReadOnly" | "Full">;
  tracks: Record<string, TrackPlaybackState>;
};

export type MapStoreActions = {
  patch(partial: Partial<MapStoreState>): void;
  setLayers(layers: LayerSnapshot[]): void;
  updateLayer(id: string, patch: Partial<LayerSnapshot>): void;
  setInfo(info: InfoState): void;
  setPanel(panel: PanelName): void;
  toggleCollapsed(key: string): void;
  pushToast(toast: Omit<Toast, "id">): number;
  dismissToast(id: number): void;
};

export type MapStore = StoreApi<MapStoreState & MapStoreActions>;

export function emptyDrawGeometry(): DrawGeometry {
  return { mode: null, wkt: null, points: [], lengthMeters: 0, areaSquareMeters: 0, perimeterMeters: 0, valid: false, reason: null };
}

export function emptyResults(): ResultsState {
  return { source: null, term: "", items: [], capped: [], loading: false, error: null };
}

export function initialState(partial: Partial<MapStoreState> = {}): MapStoreState {
  return {
    ready: false,
    catalogue: null,
    catalogueError: null,
    layers: [],
    collapsed: [],
    dark: false,
    theme: "system",
    langId: 1,
    view: { center: [46.9067, 19.6913], zoom: 13, bounds: null },
    selection: { ids: [], objects: [] },
    hovered: null,
    draw: { geometry: emptyDrawGeometry(), phase: "idle", canUndo: false, canRedo: false, snap: true, editable: true },
    info: { kind: "closed" },
    search: { term: "", tab: "objects", loading: false, objects: [], addresses: [], recent: [], error: null, filterFields: [], filters: {} },
    results: emptyResults(),
    sideTab: "layers",
    measure: { mode: null, points: [], summary: null },
    panel: null,
    panelPinned: false,
    tableLayer: null,
    inspectorModal: false,
    sideClosed: false,
    hint: null,
    coordinateSystem: "wgs84",
    cursor: null,
    toasts: [],
    errors: [],
    editing: false,
    featureAccess: {},
    tracks: {},
    ...partial
  };
}

let toastCounter = 0;

export function createMapStore(partial: Partial<MapStoreState> = {}): MapStore {
  return createStore<MapStoreState & MapStoreActions>()((set, get) => ({
    ...initialState(partial),
    patch: p => set(p),
    setLayers: layers => set({ layers }),
    updateLayer: (id, patch) => set({ layers: get().layers.map(a => (a.id === id ? { ...a, ...patch } : a)) }),
    setInfo: info => set({ info }),
    setPanel: panel => set({ panel }),
    toggleCollapsed: key => {
      const current = get().collapsed;
      set({ collapsed: current.includes(key) ? current.filter(a => a !== key) : [...current, key] });
    },
    pushToast: toast => {
      const id = ++toastCounter;
      set({ toasts: [...get().toasts, { ...toast, id }] });
      return id;
    },
    dismissToast: id => set({ toasts: get().toasts.filter(a => a.id !== id) })
  }));
}
